import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { apiErrorMessage } from '../services/api';

const UserHistory = ({ userId }) => {
  const [history, setHistory] = useState({
    joined: [],
    created: [],
    upcoming: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [error, setError] = useState('');

  // GET /api/history/events returns models.Event rows; map them onto the shape
  // this component renders. `rating` and `feedback` are intentionally absent --
  // the API does not expose per-event reviews yet, and those blocks are guarded.
  const toItem = (raw, isCreator) => {
    const start = raw.start_time || raw.StartTime;
    const datetime = start ? new Date(start) : null;
    const isPast = datetime ? datetime.getTime() < Date.now() : false;
    return {
      id: raw.id || raw.ID,
      title: raw.title || raw.Title || 'Untitled CliQQ',
      datetime,
      location: raw.location || raw.Location || 'TBD',
      attendees: raw.current_attendees ?? raw.CurrentAttendees ?? 0,
      maxAttendees: raw.max_attendees ?? raw.MaxAttendees ?? 0,
      isCreator,
      status: isPast ? 'completed' : 'confirmed',
    };
  };

  const fetchUserHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // This endpoint returns {data, pagination}, not the {success, message,
      // data} envelope the rest of the API uses.
      const [createdRes, joinedRes] = await Promise.all([
        api.get('/api/history/events', { params: { type: 'created', limit: 50 } }),
        api.get('/api/history/events', { params: { type: 'joined', limit: 50 } }),
      ]);

      const created = (createdRes.data?.data || []).map((e) => toItem(e, true));
      const joined = (joinedRes.data?.data || []).map((e) => toItem(e, false));

      // An event you host also appears under "joined" if you registered for it,
      // so dedupe by id before building the upcoming list.
      const byId = new Map();
      [...created, ...joined].forEach((e) => {
        if (e.id != null && !byId.has(e.id)) byId.set(e.id, e);
      });
      const upcoming = [...byId.values()]
        .filter((e) => e.datetime && e.datetime.getTime() > Date.now())
        .sort((a, b) => a.datetime - b.datetime);

      setHistory({ created, joined, upcoming });
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load your CliQQ history'));
      setHistory({ created: [], joined: [], upcoming: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserHistory();
  }, [fetchUserHistory, userId]);

  const formatDateTime = (date) => {
    if (!date) return 'Date TBD';
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const renderStarRating = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  const calculateAverageRating = (feedback) => {
    if (!feedback || feedback.length === 0) return 0;
    const total = feedback.reduce((sum, item) => sum + item.rating, 0);
    return (total / feedback.length).toFixed(1);
  };

  const renderEventCard = (event) => (
    <div key={event.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <Link to={`/events/${event.id}`} className="text-lg font-semibold text-gray-900 hover:text-primary-600">
            {event.title}
          </Link>
          {event.isCreator && (
            <span className="ml-2 px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded-full">
              Creator
            </span>
          )}
        </div>
        {getStatusBadge(event.status)}
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {formatDateTime(event.datetime)}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {event.location}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          {event.attendees}/{event.maxAttendees} attendees
        </div>
      </div>

      {/* Show rating for joined events */}
      {activeTab === 'joined' && event.rating && (
        <div className="mb-4">
          <span className="text-sm text-gray-600 mr-2">Your rating:</span>
          {renderStarRating(event.rating)}
        </div>
      )}

      {/* Show feedback summary for created events */}
      {activeTab === 'created' && event.feedback && event.feedback.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <span className="text-sm text-gray-600 mr-2">Average rating:</span>
            {renderStarRating(parseFloat(calculateAverageRating(event.feedback)))}
          </div>
          <div className="text-xs text-gray-500">
            {event.feedback.length} review{event.feedback.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <Link
          to={`/events/${event.id}`}
          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
        >
          View Details
        </Link>
        {event.status === 'completed' && !event.rating && activeTab === 'joined' && (
          <button className="text-sm bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700">
            Rate Event
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6">Event History</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200">
          {[
            { key: 'upcoming', label: 'Upcoming', count: history.upcoming.length },
            { key: 'joined', label: 'Joined Events', count: history.joined.length },
            { key: 'created', label: 'Created Events', count: history.created.length }
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`pb-2 px-1 font-medium text-sm border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {history[activeTab].length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1">
              {history[activeTab].map(renderEventCard)}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {activeTab === 'upcoming' ? 'No upcoming events' : 
                 activeTab === 'joined' ? 'No events joined yet' : 
                 'No events created yet'}
              </h3>
              <p className="mt-2 text-gray-600">
                {activeTab === 'upcoming' ? 'Join some events to see them here!' : 
                 activeTab === 'joined' ? 'Start joining events to build your history.' : 
                 'Create your first event to get started.'}
              </p>
              {activeTab !== 'upcoming' && (
                <Link
                  to={activeTab === 'joined' ? '/join' : '/create-event'}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  {activeTab === 'joined' ? 'Browse Events' : 'Create Event'}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserHistory;